import { ArrowUpRight, CalendarClock, Landmark, Mail, Shield, ShieldCheck, Flame } from "lucide-react";
import Reveal from "@/components/Reveal";

const MEMBERSHIP_FORM_URL = "https://templebethelwpb.shulcloud.com/form/2026-2027-temple-beth-el-membership-form";

const SUSTAINING_LEVELS = [
  {
    id: "magen",
    icon: Shield,
    name: "Magen Beth El",
    tagline: "Protector of Beth El",
    badge: "Highest Level",
    family: "$12,000+",
    individual: "$6,000+",
    benefits: [
      "Includes Friday Night Happenings",
      "Beth El-sponsored events at no additional charge (events priced above $150 offered at a 50% discount)",
      "High Holiday dinners are included",
      "High Holy Day parking is included",
    ],
  },
  {
    id: "bonim",
    icon: Landmark,
    name: "Bonim Beth El",
    tagline: "Builders of Beth El",
    badge: null,
    family: "$6,000+",
    individual: "$3,000+",
    benefits: [
      "Includes Friday Night Happenings",
      "50% discount on Beth El-sponsored events",
      "50% discount on High Holy Day parking",
    ],
  },
  {
    id: "koach",
    icon: Flame,
    name: "Koach Beth El",
    tagline: "Strength of Beth El",
    badge: null,
    family: "$4,320+",
    individual: "$2,160+",
    benefits: [
      "Includes Friday Night Happenings",
      "25% discount on Beth El-sponsored events",
      "25% discount on High Holy Day parking",
    ],
  },
];

function SustainingMembership() {
  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-24 md:mt-32" data-testid="sustaining-membership">
      <Reveal>
        <p className="text-[10px] md:text-xs tracking-[0.35em] uppercase text-gold mb-4">A Message from the President</p>
        <h3 className="font-syne font-bold uppercase tracking-tighter leading-[0.95] text-3xl md:text-5xl mb-6">
          Your membership. Your community. <span className="text-gold">Our future.</span>
        </h3>
        <div className="max-w-3xl space-y-4 text-sm md:text-base text-foreground/70 leading-relaxed" data-testid="president-message">
          <p>Dear Beth El Family,</p>
          <p>
            As we begin our second century, Beth El is becoming more than a place we come for services. Our vision for
            the Beth El Center for Jewish Life is to create a vibrant Jewish home where we can pray, learn, celebrate,
            connect, and build relationships throughout the year.
          </p>
          <p>Making that vision possible requires the support of our members.</p>
          <p>
            Our Sustaining Membership levels — <strong className="text-bone">Koach Beth El</strong>,{" "}
            <strong className="text-bone">Bonim Beth El</strong> and <strong className="text-bone">Magen Beth El</strong> —
            were created for members who are able and willing to make an additional commitment to Beth El. These
            memberships help support our worship, education, security, family programming, cultural events and the many
            other experiences that make Beth El a true Center for Jewish Life. That is their most important benefit.
          </p>
          <p>At the same time, we want to recognize that additional commitment with meaningful benefits.</p>
        </div>
      </Reveal>

      <Reveal>
        <p className="mt-14 mb-6 text-center text-[10px] md:text-xs tracking-[0.35em] uppercase text-gold" data-testid="sustaining-levels-heading">
          Sustaining Membership Levels &amp; Annual Commitment
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {SUSTAINING_LEVELS.map((lv) => (
            <div key={lv.id} className="border border-gold/40 bg-surface p-7 flex flex-col" data-testid={`sustaining-level-${lv.id}`}>
              <lv.icon size={26} className="text-gold mb-4" aria-hidden="true" />
              <h4 className="font-syne font-extrabold uppercase tracking-tight text-lg">{lv.name}</h4>
              <p className="font-cormorant italic text-gold/90">{lv.tagline}</p>
              {lv.badge && <p className="text-[10px] tracking-[0.25em] uppercase text-foreground/50 mt-1">({lv.badge})</p>}
              <div className="mt-5 mb-5 border-y border-white/10 py-4 grid grid-cols-2 gap-3">
                <div>
                  <p className="font-syne font-extrabold text-xl text-gold">{lv.family}</p>
                  <p className="text-[9px] tracking-[0.2em] uppercase text-foreground/50 mt-1">Annual Family Commitment</p>
                </div>
                <div>
                  <p className="font-syne font-extrabold text-xl text-gold">{lv.individual}</p>
                  <p className="text-[9px] tracking-[0.2em] uppercase text-foreground/50 mt-1">Annual Individual Commitment</p>
                </div>
              </div>
              <ul className="space-y-2.5 text-xs md:text-sm text-foreground/70 leading-relaxed flex-1">
                {lv.benefits.map((b) => (
                  <li key={b} className="flex gap-2.5">
                    <span className="w-1 h-1 rounded-full bg-gold mt-2 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
              <p className="mt-5 pt-4 border-t border-white/10 text-[11px] text-foreground/45 leading-relaxed">
                You can choose to give more — every additional gift strengthens Beth El and is deeply appreciated.
              </p>
            </div>
          ))}
        </div>
        <p className="mt-5 font-cormorant italic text-foreground/55 text-sm" data-testid="sustaining-note">
          Women's League and Men's Club programs are separate and are not included in these benefits.
        </p>
        <div className="mt-5 border border-gold/40 bg-gold/10 px-6 py-4 flex items-start gap-3" data-testid="sustaining-monthly-reminder">
          <CalendarClock size={16} className="text-gold shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm text-foreground/80 leading-relaxed">
            <strong className="text-gold">Reminder:</strong> Sustaining Membership dues can be paid <strong>monthly</strong>.
            You may choose the monthly payment option that works best for you.
          </p>
        </div>
      </Reveal>

      <Reveal>
        <div className="mt-12 max-w-3xl space-y-4 text-sm md:text-base text-foreground/70 leading-relaxed">
          <p>
            <strong className="text-bone">It is worth noting:</strong> Sustaining Membership is not simply about receiving
            benefits. Our sustaining members are making a statement that Beth El matters to them and that they want to
            help ensure its strength for the generations that follow us.
          </p>
          <p className="font-cormorant italic text-lg md:text-xl text-gold/90">
            The benefits are our way of saying thank you. The real benefit is what your support makes possible.
          </p>
          <p>
            Whether you choose a traditional membership or one of our Sustaining Membership levels, thank you for being
            part of Beth El. And if you are in a position to become a Koach, Bonim or Magen member, I hope you will
            consider making that additional commitment.
          </p>
          <p>Together, we are building the next century of Jewish life at Beth El.</p>
          <p className="pt-2">
            With gratitude,
            <span className="block font-cormorant italic text-2xl text-gold mt-1">Scott Levy</span>
            <span className="block text-xs text-foreground/50 mt-1">President · Beth El Center for Jewish Life</span>
          </p>
        </div>
        <div className="mt-10 flex flex-wrap items-center gap-6">
          <a
            href={MEMBERSHIP_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sustaining-join-cta"
            className="inline-flex items-center gap-3 px-8 py-4 bg-gold text-inkdeep text-xs tracking-[0.25em] uppercase font-medium hover:bg-gold-light transition-colors"
          >
            Join or Renew Membership <ArrowUpRight size={14} aria-hidden="true" />
          </a>
          <p className="inline-flex items-center gap-2 text-xs text-foreground/50" data-testid="sustaining-questions">
            <Mail size={13} className="text-gold/70" aria-hidden="true" />
            Questions? Contact the Beth El Center office at{" "}
            <a href="tel:+15618330339" className="text-gold hover:underline">(561) 833-0339</a> or{" "}
            <a href="mailto:office@bethelwpb.org" className="text-gold hover:underline">office@bethelwpb.org</a>
          </p>
        </div>
        <p className="mt-12 text-center text-[10px] tracking-[0.45em] uppercase text-gold/70">
          Pray · Learn · Celebrate · Connect
        </p>
      </Reveal>
    </div>
  );
}

export default function Membership() {
  return (
    <section id="join" data-testid="membership-section" className="py-32 md:py-48 border-t border-white/10 bg-surface/40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-center">
        <Reveal className="lg:col-span-7">
          <p className="text-[10px] md:text-xs tracking-[0.35em] uppercase text-gold mb-4">Membership</p>
          <h2 className="font-syne font-bold uppercase tracking-tighter leading-[0.95] text-4xl md:text-6xl mb-8">
            Become part of the <span className="text-gold">next hundred years</span>
          </h2>
          <p className="text-base md:text-lg text-foreground/70 leading-relaxed max-w-xl">
            Membership means a seat at the table — Shabbat dinners, lifelong learning, holiday celebrations, and a
            community that shows up for every chapter of life. Joining or renewing takes just a few minutes.
          </p>
          <p className="mt-8 font-cormorant italic text-xl text-gold/90">
            "Every individual is embraced as family."
          </p>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="border border-gold/40 bg-surface p-8 md:p-10" data-testid="membership-card">
            <h3 className="font-syne font-bold uppercase tracking-tight text-lg mb-3">Join or renew for 2026&ndash;2027</h3>
            <p className="text-sm text-foreground/60 leading-relaxed mb-8" data-testid="membership-explainer">
              Our membership application is handled securely through ShulCloud, Beth El&rsquo;s member portal — including
              your membership category and payment. The Beth El office is notified as soon as your submission goes
              through and will follow up with a warm welcome.
            </p>
            <a
              href={MEMBERSHIP_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="membership-join-cta"
              className="w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-gold text-inkdeep text-xs tracking-[0.25em] uppercase font-medium hover:bg-gold-light transition-colors"
            >
              Join or Renew Membership <ArrowUpRight size={14} aria-hidden="true" />
            </a>
            <p className="mt-6 inline-flex items-start gap-2 text-xs text-foreground/45 leading-relaxed">
              <ShieldCheck size={14} className="text-gold/70 shrink-0 mt-0.5" aria-hidden="true" />
              Opens in a new tab. Questions first? Call the office at{" "}
              <a href="tel:+15618330339" className="text-gold hover:underline">(561)&nbsp;833-0339</a>.
            </p>
          </div>
        </Reveal>
      </div>
      <SustainingMembership />
    </section>
  );
}
